import { SENSOR_SIZE, TEST_COMPANIES } from "@/constants";
import * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import pinpointSvg from "@/assets/icons/pinpoint.svg";
import { ElMessage } from "element-plus";
import { event } from "vue-gtag";
import { isProduction, isStaging } from "@/utils/env";

export const initializeGalleryMode = async function (imageSize, imagesCount) {
  if (!this.thumbnailsAvailable) {
    return this.restoreDefaultCursor();
  }

  this.originalImageWidth = imageSize.width;
  this.originalImageHeight = imageSize.height;

  if (!this.cameraMesh) {
    await this.fetchCameras();
  }

  if (this.csvAvailable) {
    const completenessTestPassed = this.runCompletenessTest(imagesCount);
    if (!completenessTestPassed)
      return this.useLimitiedGalleryMode(imagesCount);

    if (this.camerasFrustums.length === 0) {
      this.camerasFrustums = this.camerasCsv.map((camera, index) =>
        this.createFrustumForCamera(camera, index)
      );
    }
    document.addEventListener("click", this.selectPointOnModel, false);
    if (this.workerInitialized === null) this.initializeCameraWorkers();

    this.changeCursorToPointer();
  } else {
    this.useLimitiedGalleryMode(imagesCount);
  }
};

export const runCompletenessTest = function (imagesCount) {
  return this.camerasCsv.length === imagesCount;
};

export const useLimitiedGalleryMode = function (imagesCount) {
  this.restoreDefaultCursor();
  if (this.galleryWarningShown) return;
  ElMessage.warning({
    message: this.$t("imageNavigation.limitedGalleryMode"),
    showClose: true,
    duration: 0,
  });
  this.galleryWarningShown = true;

  if ((isProduction && !TEST_COMPANIES.includes(this.companyId)) || isStaging) {
    event("limited_gallery_mode", {
      project_id: this.project.id,
      company_id: this.company.id,
      anonymous_user: this.anonymousUser,
      customer_type: this.currentUser.isEmployee ? "employee" : "customer",
      reason: this.csvAvailable ? "completeness_test_failed" : "csv_missing",
      images_count: imagesCount,
      cameras_count: this.csvAvailable ? this.camerasCsv.length : 0,
    });
  }
};

export const selectPointOnModel = async function (event) {
  event.preventDefault();

  if (this.disableClick(event)) return;

  this.mousePositionOnScreen = {
    x: event.clientX,
    y: event.clientY,
  };

  this.setMousePosition(event);

  // Remove previous gallery point more thoroughly
  if (this.galleryPoint) {
    this.removeGalleryPoint();
  }

  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0]
  );

  if (intersects.length < 1) {
    this.resetBestImage();
    return;
  }

  this.changeCursorToLoading();
  this.galleryPointsSelected++;

  const position = intersects[0].point;

  this.galleryPoint = this.addImageMarkerToScene(position);

  const camerasIncludingPoint = this.getCamerasIncludingPoint(position);

  const bestFitCamera = await this.getBestFitCamera(
    camerasIncludingPoint,
    position
  );

  const cameraIndex = this.camerasFrustums.findIndex(
    (camera) => camera.cameraData["#name"] === bestFitCamera.cameraData["#name"]
  );

  // reset bestImage value to fire the watcher
  this.bestImage = null;
  this.$nextTick(() => {
    this.bestImage = cameraIndex;
    this.restoreDefaultCursor();
  });

  return bestFitCamera;
};

export const getCamerasIncludingPoint = function (point) {
  return this.camerasFrustums.filter((camera) =>
    camera.frustum.containsPoint(point)
  );
};

export const getBestFitCamera = async function (cameras, point) {
  if (!cameras || cameras.length === 0) {
    return;
  }

  await this.getCamerasWithClearLineOfSight(cameras, point);

  let candidateCameras = cameras.filter((camera) => camera.hasClearLineOfSight);

  this.calculateNormalizedDistanceToPoint(cameras);

  candidateCameras.forEach((camera) => {
    camera.coordinatesOnImage = this.getPointCoordinatesOnImage(camera, point);

    camera.normalizedDistanceToCenter = this.calculateDistanceFromCenter(
      camera.coordinatesOnImage
    );
    camera.score = this.calculateBestFitScore(camera);
  });
  this.bestImagesPoints = candidateCameras;

  const bestFitCamera = candidateCameras.reduce(
    (best, camera) => (camera.score > best.score ? camera : best),
    candidateCameras[0]
  );

  return bestFitCamera;
};

export const getCamerasWithClearLineOfSight = function (cameras, point) {
  return new Promise((resolve) => {
    const allWorkersReady = this.workerInitialized.every((ready) => ready);
    if (!allWorkersReady) {
      setTimeout(() => {
        this.getCamerasWithClearLineOfSight(cameras, point).then(resolve);
      }, 100);
    }

    const workerCount = this.cameraWorkers.length;

    const chunkSize = Math.ceil(cameras.length / workerCount);
    const chunks = [];

    for (let i = 0; i < workerCount; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, cameras.length);
      if (start < cameras.length) {
        chunks.push(cameras.slice(start, end));
      }
    }

    this.workerResults = new Array(chunks.length).fill(null);
    this.totalWorkers = chunks.length;
    this.lineOfSightResolve = resolve;
    this.isProcessingLineOfSight = true;

    chunks.forEach((chunk, workerIndex) => {
      const cameraData = chunk.map((camera) => ({
        originalIndex: camera.originalIndex,
        position: {
          x: camera.virtualCamera.position.x,
          y: camera.virtualCamera.position.y,
          z: camera.virtualCamera.position.z,
        },
      }));

      this.cameraWorkers[workerIndex].postMessage({
        type: "CHECK_CAMERAS_LINE_OF_SIGHT",
        data: {
          cameras: cameraData,
          point: {
            x: point.x,
            y: point.y,
            z: point.z,
          },
        },
      });
    });
  });
};

export const getPointCoordinatesOnImage = function (camera, point) {
  const image = document.querySelector(`.image-thumbnail`);
  if (!image) throw new Error("No image found");

  const intrinsics = this.computeFullIntrinsics(
    camera.cameraData,
    this.originalImageWidth,
    this.originalImageHeight
  );
  const pointInPixel = this.projectWithDistortion(
    point,
    camera.virtualCamera,
    intrinsics
  );

  return pointInPixel;
};

export const calculateOffsetForFullScreen = function (image) {
  const contentBox = image.getBoundingClientRect();
  const renderedSize = this.getImageRenderedSize(image);

  const offsetX = (contentBox.width - renderedSize.width) / 2;
  const offsetY = (contentBox.height - renderedSize.height) / 2;

  return {
    offsetX,
    offsetY,
  };
};

export const calculateDistanceFromCenter = function (pointCoordinates) {
  const image = document.querySelector(".image-thumbnail");
  if (!image) throw new Error("No image found");

  const imageWidth = image.clientWidth;
  const imageHeight = image.clientHeight;
  const imageCenter = {
    x: imageWidth / 2,
    y: imageHeight / 2,
  };

  const distanceToCenter = Math.sqrt(
    Math.pow(pointCoordinates.x * imageWidth - imageCenter.x, 2) +
      Math.pow(pointCoordinates.y * imageHeight - imageCenter.y, 2)
  );

  const normalizedDistance =
    distanceToCenter /
    Math.sqrt(Math.pow(imageWidth / 2, 2) + Math.pow(imageHeight / 2, 2));

  return normalizedDistance;
};

export const calculateNormalizedDistanceToPoint = function (cameras) {
  const maxDistanceToPoint = cameras.reduce(
    (max, camera) => Math.max(max, camera.distanceToPoint),
    0
  );

  cameras.forEach((camera) => {
    camera.normalizedDistanceToPoint =
      maxDistanceToPoint === 0
        ? 0
        : camera.distanceToPoint / maxDistanceToPoint;
  });
};

export const calculateBestFitScore = function (camera) {
  const { normalizedDistanceToCenter, normalizedDistanceToPoint } = camera;

  const centerScore = 1 - normalizedDistanceToCenter;
  const distanceScore = 1 - normalizedDistanceToPoint;

  return 0.5 * centerScore + 0.5 * distanceScore;
};

export const createFrustumForCamera = function (camera, index) {
  const worldMatrix = this.transformCamera(camera);

  // 2. Decompose the matrix into its position, quaternion, and scale components.
  const worldPosition = new THREE.Vector3();
  const worldQuaternion = new THREE.Quaternion();
  const worldScale = new THREE.Vector3(); // We don't need scale for the camera, but decompose extracts it.

  worldMatrix.decompose(worldPosition, worldQuaternion, worldScale);
  return {
    ...this.getFrustumFromCamera(camera, index),
    originalIndex: index,
  };
};

export const getFrustumFromCamera = function (
  camera,
  index,
  near = 0.1,
  far = 100
) {
  const image = document.querySelector(".image-thumbnail");
  if (!image) throw new Error("No image found");

  const { f, heading, pitch, roll, x, y, alt } = camera;

  const aspect = this.calculateAspectRatio(
    this.originalImageWidth,
    this.originalImageHeight
  );

  const fov = this.calculateFov(f, aspect);

  const relativePosition = new THREE.Vector3(x, y, alt);
  const defaultXRotation = this.exportedManually ? -Math.PI / 2 : Math.PI / 2;
  const rotationMatrix = new THREE.Matrix4().makeRotationX(defaultXRotation);
  relativePosition.applyMatrix4(rotationMatrix);

  const modelMatrix = this.modelObject.children[0].matrixWorld;
  const finalPosition = relativePosition.clone().applyMatrix4(modelMatrix);

  const pitchRadians = THREE.MathUtils.degToRad(pitch);
  const headingRadians = THREE.MathUtils.degToRad(heading);
  const rollRadians = THREE.MathUtils.degToRad(roll);

  const cameraEuler = new THREE.Euler(
    pitchRadians,
    rollRadians,
    -headingRadians,
    "ZXY"
  );

  const cameraQuaternion = new THREE.Quaternion().setFromEuler(cameraEuler);

  const modelQuaternion = new THREE.Quaternion();
  this.modelObject.children[0].getWorldQuaternion(modelQuaternion);

  const xRotationQuaternion = new THREE.Quaternion();
  xRotationQuaternion.setFromAxisAngle(
    new THREE.Vector3(1, 0, 0),
    defaultXRotation
  );

  const finalQuaternion = new THREE.Quaternion()
    .copy(modelQuaternion)
    .multiply(xRotationQuaternion)
    .multiply(cameraQuaternion);

  const virtualCamera = new THREE.PerspectiveCamera(fov, aspect, near, far);
  virtualCamera.position.copy(finalPosition);
  virtualCamera.quaternion.copy(finalQuaternion);

  virtualCamera.updateMatrixWorld(true);
  virtualCamera.updateProjectionMatrix();

  const frustum = new THREE.Frustum();
  const matrix = new THREE.Matrix4().multiplyMatrices(
    virtualCamera.projectionMatrix,
    virtualCamera.matrixWorldInverse
  );
  frustum.setFromProjectionMatrix(matrix);

  return { frustum, virtualCamera, cameraData: camera };
};

export const visualizeVirtualCamera = function (virtualCamera) {
  const helper = new THREE.CameraHelper(virtualCamera);
  this.scene.add(helper);
};

export const calculateFov = function (focalLength35mm) {
  const fovY = 2 * Math.atan(SENSOR_SIZE.FILM_HEIGHT / (2 * focalLength35mm));
  return THREE.MathUtils.radToDeg(fovY);
};

export const calculateAspectRatio = function (imageWidth, imageHeight) {
  return imageWidth / imageHeight;
};

export const calculateCameraQuaternion = function (pitch, heading, roll) {
  const pitchRadians = THREE.MathUtils.degToRad(pitch);
  const headingRadians = THREE.MathUtils.degToRad(heading);
  const rollRadians = THREE.MathUtils.degToRad(roll);

  return new THREE.Quaternion().setFromEuler(
    new THREE.Euler(pitchRadians, rollRadians, -headingRadians, "ZXY")
  );
};

export const removeGalleryPoint = function () {
  if (this.galleryPoint) {
    this.removeObjectFromScene(this.galleryPoint);
    this.galleryPoint = null;
  }
};

export const toggleFullScreenActive = function (value) {
  this.fullScreenImageActive = value;

  if (value) {
    this.renderer.setAnimationLoop(null);
  } else {
    this.renderer.setAnimationLoop(() => {
      this.renderer.render(this.scene, this.camera);

      if (this.controls && this.controls.update) this.controls.update();
    });
  }
};

export const resetBestImage = function () {
  this.bestImagesPoints = [];
  this.bestImage = null;
};

export const addImageMarkerToScene = function (position) {
  // Create DOM element for the pinpoint icon
  const pinpointIcon = document.createElement("img");
  pinpointIcon.src = pinpointSvg;

  // Use fixed size - no dynamic scaling
  const iconSize = 24; // Fixed size in pixels

  pinpointIcon.style = `width: ${iconSize}px; height: ${iconSize}px; pointer-events: none;`;

  const pinpointDiv = document.createElement("div");
  pinpointDiv.style = "pointer-events: none;";
  pinpointDiv.append(pinpointIcon);

  // Create CSS2DObject for the pinpoint
  const pinpointLabel = new CSS2DObject(pinpointDiv);
  pinpointLabel.position.copy(position);
  pinpointLabel.layers.set(0);
  pinpointLabel.renderOrder = 9999999;

  this.scene.add(pinpointLabel);

  return pinpointLabel;
};

export const getImageRenderedSize = function (img) {
  const style = window.getComputedStyle(img);

  const renderedWidth =
    img.clientWidth -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight);

  const renderedHeight =
    img.clientHeight -
    parseFloat(style.paddingTop) -
    parseFloat(style.paddingBottom);

  return {
    width: renderedWidth,
    height: renderedHeight,
  };
};

export const computeFullIntrinsics = function (
  cameraData,
  imageWidth,
  imageHeight
) {
  const { f, px, py, k1, k2, k3 } = cameraData;

  const pixelsPerMmX = imageWidth / SENSOR_SIZE.FILM_WIDTH;
  const pixelsPerMmY = imageHeight / SENSOR_SIZE.FILM_HEIGHT;

  const fx = f * pixelsPerMmX;
  const fy = f * pixelsPerMmY;

  const cx = imageWidth / 2 + px * pixelsPerMmX;
  const cy = imageHeight / 2 - py * pixelsPerMmY;

  return { fx, fy, cx, cy, k1, k2, k3 };
};

export const projectWithDistortion = function (
  point3D,
  virtualCamera,
  intrinsics
) {
  const pointInCameraSpace = point3D
    .clone()
    .applyMatrix4(virtualCamera.matrixWorldInverse);

  if (pointInCameraSpace.z >= 0) return null;

  const x3 = pointInCameraSpace.x;
  const y3 = -pointInCameraSpace.y;
  const z3 = -pointInCameraSpace.z;

  const x_n = x3 / z3;
  const y_n = y3 / z3;

  const r2 = x_n * x_n + y_n * y_n;
  const r4 = r2 * r2;
  const r6 = r4 * r2;

  const distortionFactor =
    1.0 + intrinsics.k1 * r2 + intrinsics.k2 * r4 + intrinsics.k3 * r6;

  const x_distorted = x_n * distortionFactor;
  const y_distorted = y_n * distortionFactor;

  const final_x = intrinsics.fx * x_distorted + intrinsics.cx;
  const final_y = intrinsics.fy * y_distorted + intrinsics.cy;

  return new THREE.Vector2(
    final_x / this.originalImageWidth,
    final_y / this.originalImageHeight
  );
};

export const initializeCameraWorkers = function () {
  if (this.cameraWorkers.length > 0) {
    this.cameraWorkers.forEach((worker) => worker.terminate());
  }

  const workerCount = 4;
  this.workerInitialized = new Array(workerCount).fill(false);

  for (let i = 0; i < workerCount; i++) {
    const cameraWorker = new Worker(
      new URL("../workers/camera-worker.js", import.meta.url),
      { type: "module" }
    );

    cameraWorker.onmessage = (e) => {
      const { type, data, workerId } = e.data;

      switch (type) {
        case "INIT_COMPLETE":
          this.workerInitialized[workerId] = true;
          break;
        case "COMPLETE":
          this.handleCameraWorkerResults(data, workerId);
          break;
      }
    };

    this.cameraWorkers.push(cameraWorker);
  }

  this.initCameraWorkers();
};

export const initCameraWorkers = function () {
  const tolerance = this.calculateDynamicTolerance();

  const model = this.modelObject.children[0];
  const geometry = model.geometry;
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;

  this.cameraWorkers.forEach((worker, index) => {
    worker.postMessage({
      type: "INIT",
      workerId: index,
      data: {
        modelData: {
          positions: positions,
          indices: indices,
          rotation: {
            x: this.project.orientation.x,
            y: this.project.orientation.y,
            z: this.project.orientation.z,
          },
          scale: this.project.scale,
          tolerance,
        },
      },
    });
  });
};

export const handleCameraWorkerResults = function (results, workerId) {
  this.workerResults[workerId] = results;
  this.completedWorkers++;

  if (this.completedWorkers === this.totalWorkers) {
    const allResults = this.workerResults.flat();

    allResults.forEach((result) => {
      const camera = this.camerasFrustums.find(
        (cam) => cam.originalIndex === result.index
      );
      if (camera) {
        camera.hasClearLineOfSight = result.hasClearLineOfSight;
        camera.distanceToPoint = result.distanceToPoint;
      }
    });

    this.workerResults = null;
    this.completedWorkers = 0;
    this.totalWorkers = 0;

    this.isProcessingLineOfSight = false;
    this.lineOfSightResolve();
    this.lineOfSightResolve = null;
  }
};

export const calculateDynamicTolerance = function () {
  const bbox = new THREE.Box3().setFromObject(this.modelObject.children[0]);
  return bbox.getSize(new THREE.Vector3()).length() * 0.001;
};

export const setThumbnailsAvailable = function (images) {
  this.thumbnailsAvailable = images.every((image) => image.thumbnailAvailable);

  return this.thumbnailsAvailable;
};
